import React, { useState, useEffect } from 'react';
import { Car, Globe, MessageSquare, Users, CheckCircle, Clock, XCircle, TrendingUp, Calendar, Phone } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    cars: 0,
    tours: 0,
    carInquiries: 0,
    tourInquiries: 0,
    contacts: 0
  });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const [carsRes, toursRes, carInquiriesRes, tourInquiriesRes, contactsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/cars`, config),
        axios.get(`${API_BASE_URL}/api/admin/tours`, config),
        axios.get(`${API_BASE_URL}/api/admin/inquiries/car`, config),
        axios.get(`${API_BASE_URL}/api/admin/inquiries/tour`, config),
        axios.get(`${API_BASE_URL}/api/admin/contacts`, config)
      ]);

      setStats({
        cars: carsRes.data.length,
        tours: toursRes.data.length,
        carInquiries: carInquiriesRes.data.length,
        tourInquiries: tourInquiriesRes.data.length,
        contacts: contactsRes.data.length
      });

      const allInquiries = [
        ...carInquiriesRes.data.map(i => ({ ...i, type: 'Car Rental', item: i.car_name })),
        ...tourInquiriesRes.data.map(i => ({ ...i, type: 'Tour Booking', item: i.tour_name }))
      ];
      
      const sorted = allInquiries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRecentInquiries(sorted.slice(0, 5));
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Cars', value: stats.cars, icon: Car, color: 'from-blue-500 to-blue-600' },
    { title: 'Total Tours', value: stats.tours, icon: Globe, color: 'from-green-500 to-green-600' },
    { title: 'Car Inquiries', value: stats.carInquiries, icon: MessageSquare, color: 'from-yellow-500 to-yellow-600' },
    { title: 'Tour Inquiries', value: stats.tourInquiries, icon: Users, color: 'from-purple-500 to-purple-600' },
    { title: 'Contact Messages', value: stats.contacts, icon: MessageSquare, color: 'from-red-500 to-red-600' },
  ];

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      contacted: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-dark-900 rounded-lg p-4 hover:shadow-elegant transition border border-dark-700">
            <div className="flex items-center justify-between mb-2">
              <div className={`bg-gradient-to-r ${stat.color} p-2 rounded-lg`}>
                <stat.icon size={18} className="text-white" />
              </div>
              <span className="text-2xl font-bold text-white">{stat.value}</span>
            </div>
            <p className="text-gray-400 text-sm">{stat.title}</p>
          </div>
        ))}
      </div>

      {/* Recent Inquiries Section */}
      <div className="bg-dark-900 rounded-lg border border-dark-700 mb-6">
        <div className="p-4 border-b border-dark-700">
          <h2 className="text-lg font-bold text-white">Recent Inquiries</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Item</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {recentInquiries.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No inquiries yet
                  </td>
                </tr>
              ) : (
                recentInquiries.map((inquiry, index) => (
                  <tr key={index} className="hover:bg-dark-800 transition">
                    <td className="px-4 py-3 text-sm text-white">{inquiry.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{inquiry.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        inquiry.type === 'Car Rental' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {inquiry.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">{inquiry.item || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm capitalize px-2 py-0.5 rounded-full ${getStatusBadge(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {new Date(inquiry.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
          <h3 className="text-lg font-bold mb-2">Manage Cars</h3>
          <p className="text-blue-100 mb-4">Add, edit or remove vehicles from your fleet</p>
          <a href="/admin/cars" className="inline-block bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition">
            Go to Cars →
          </a>
        </div>
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-lg p-6 text-white">
          <h3 className="text-lg font-bold mb-2">Manage Tours</h3>
          <p className="text-green-100 mb-4">Add, edit or remove tour packages</p>
          <a href="/admin/tours" className="inline-block bg-white text-green-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition">
            Go to Tours →
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;